import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, ArrowLeft, BookOpen, Download, Menu, Settings } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import AccountSettings from "@/components/AccountSettings";
import LedProgressLine from "@/components/LedProgressLine";
import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer } from "docx";
import { saveAs } from "file-saver";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const getToken = () => {
  const token = localStorage.getItem('token');
  if (token) return token;
  const trialUser = localStorage.getItem('trial_user');
  const trialId = localStorage.getItem('trial_id');
  if (trialUser && trialId) return `trial_user_${trialId}`;
  return null;
};

const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

const LegalResearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [researching, setResearching] = useState(false);
  const [researchResults, setResearchResults] = useState(null);
  const [user, setUser] = useState(getUser());
  const [trialUser, setTrialUser] = useState(null);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [accountSettingsTab, setAccountSettingsTab] = useState('account');
  
  // Set page title
  useEffect(() => {
    document.title = "Legal Research - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);
  
  useEffect(() => {
    const trialData = localStorage.getItem('trial_user');
    if (trialData) {
      setTrialUser(JSON.parse(trialData));
    }
  }, []);

  const handleResearch = async () => {
    if (!query.trim()) {
      toast.error('Please enter a research query');
      return;
    }

    setResearching(true);
    try {
      const response = await axios.post(
        `${API}/legal-research`,
        { query: query },
        {
          headers: { Authorization: `Bearer ${getToken()}` }
        }
      );

      setResearchResults(response.data);
      toast.success('Research completed successfully by Lawra!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error conducting research. Please try again.';
      
      // Check if it's a trial limit error
      if (error.response?.status === 403 && errorMessage.includes('Trial limit')) {
        toast.error(errorMessage);
        setTimeout(() => {
          navigate('/dashboard?open=account-settings&tab=billing');
        }, 500);
      } else {
        toast.error(errorMessage);
      }
      
      console.error('Research error:', error);
    } finally {
      setResearching(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (!researchResults) return;

    try {
      // Strip HTML tags from text
      const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      };

      // Create document sections
      const children = [];

      // Title
      children.push(
        new Paragraph({
          text: "Legal Research Report",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 }
        })
      );

      // Query
      children.push(
        new Paragraph({
          text: "Research Query:",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          text: researchResults.query,
          spacing: { after: 300 }
        })
      );

      // Summary
      if (researchResults.summary) {
        children.push(
          new Paragraph({
            text: "Summary",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          }),
          new Paragraph({
            text: stripHtml(researchResults.summary),
            spacing: { after: 300 }
          })
        );
      }

      // Key Findings
      if (researchResults.key_findings && researchResults.key_findings.length > 0) {
        children.push(
          new Paragraph({
            text: "Key Findings",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          })
        );
        researchResults.key_findings.forEach((finding, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${stripHtml(finding)}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            })
          );
        });
        children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
      }

      // Relevant Laws
      if (researchResults.relevant_laws && researchResults.relevant_laws.length > 0) {
        children.push(
          new Paragraph({
            text: "Relevant Laws & Statutes",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          })
        );
        researchResults.relevant_laws.forEach((law, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${stripHtml(law)}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            })
          );
        });
        children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
      }

      // Case Law
      if (researchResults.case_law && researchResults.case_law.length > 0) {
        children.push(
          new Paragraph({
            text: "Case Law & Precedents",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          })
        );
        researchResults.case_law.forEach((caseItem, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${stripHtml(caseItem)}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            })
          );
        });
        children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
      }

      // Recommendations
      if (researchResults.recommendations) {
        children.push(
          new Paragraph({
            text: "Recommendations",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          }),
          new Paragraph({
            text: stripHtml(researchResults.recommendations),
            spacing: { after: 300 }
          })
        );
      }

      // Footer
      children.push(
        new Paragraph({
          text: `Generated by Lawra on ${new Date(researchResults.created_at).toLocaleDateString()}`,
          alignment: AlignmentType.CENTER,
          spacing: { before: 400 }
        })
      );

      // Create document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children
        }]
      });

      // Generate and download
      const blob = await Packer.toBlob(doc);
      const filename = `Legal_Research_${new Date().toISOString().split('T')[0]}.docx`;
      saveAs(blob, filename);
      
      toast.success('Research downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error downloading document. Please try again.');
    }
  };

  const handleLogout = () => {
    if (trialUser) {
      navigate('/auth', { state: { showRegister: true, trialUserData: trialUser } });
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} />
          </div>
          
          <div className="header-right">
            {/* Mobile-only logo */}
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image mobile-only" />
            
            <span className="user-name">
              Hello, <span className="gold-text">{trialUser?.name || user?.full_name}</span>
            </span>
            {trialUser && (
              <span className="trial-badge">Trial Mode</span>
            )}
            
            <Button 
              onClick={handleLogout}
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)',
                color: '#1a1a1a',
                border: 'none',
                fontWeight: '700',
                padding: '0.75rem 2rem',
                borderRadius: '50px',
                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                animation: 'signupPulse 2s ease-in-out infinite',
                whiteSpace: 'nowrap',
                minWidth: '100px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #FFCE45 0%, #E5B200 100%)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(212, 175, 55, 0.8)';
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                e.currentTarget.style.animation = 'none';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.4)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.animation = 'signupPulse 2s ease-in-out infinite';
              }}
            >
              {trialUser ? 'Sign Up' : 'Logout'}
            </Button>

            {/* Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="menu-button">
                  <Menu size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="menu-dropdown">
                <DropdownMenuItem onClick={() => setAccountSettingsOpen(true)}>
                  <Settings size={16} className="mr-2" />
                  Account Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <BookOpen size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Legal Research" />
            <p className="tool-subtitle">In-depth legal research powered by Lawra's AI</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        <Card className="research-form-card">
          <CardHeader>
            <CardTitle>Research Query</CardTitle>
            <CardDescription>
              Describe what you need to research. Lawra will conduct in-depth analysis on case law, statutes, and legal precedents.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="form-group">
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g., What are the legal requirements for enforcing a debt recovery claim in England & Wales? Include relevant case law and statutory provisions."
                rows={8}
                className="gold-input research-input"
              />
            </div>

            <div className="form-actions">
              <AnimatedActionButton
                onClick={handleResearch}
                disabled={researching || !query.trim()}
                loading={researching}
                size="lg"
              >
                Start Research
              </AnimatedActionButton>
              <LedProgressLine isActive={researching} />
            </div>
          </CardContent>
        </Card>

        {researchResults && (
          <Card className="result-card">
            <CardHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <CardTitle>Research Results</CardTitle>
                  <CardDescription>
                    ✨ Researched by Lawra
                  </CardDescription>
                </div>
                <Button
                  onClick={handleDownloadDocx}
                  variant="outline"
                  className="download-btn"
                >
                  <Download size={18} style={{ marginRight: '8px' }} />
                  Download DOCX
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="research-content">
                <div className="research-section">
                  <h3 className="section-title gold-text">Summary</h3>
                  <div dangerouslySetInnerHTML={{ __html: researchResults.summary }} />
                </div>

                {researchResults.key_findings && researchResults.key_findings.length > 0 && (
                  <div className="research-section">
                    <h3 className="section-title gold-text">Key Findings</h3>
                    <ul className="findings-list">
                      {researchResults.key_findings.map((finding, index) => (
                        <li key={index} dangerouslySetInnerHTML={{ __html: finding }} />
                      ))}
                    </ul>
                  </div>
                )}

                {researchResults.relevant_laws && researchResults.relevant_laws.length > 0 && (
                  <div className="research-section">
                    <h3 className="section-title gold-text">Relevant Laws & Statutes</h3>
                    <ul className="laws-list">
                      {researchResults.relevant_laws.map((law, index) => (
                        <li key={index} dangerouslySetInnerHTML={{ __html: law }} />
                      ))}
                    </ul>
                  </div>
                )}

                {researchResults.case_law && researchResults.case_law.length > 0 && (
                  <div className="research-section">
                    <h3 className="section-title gold-text">Case Law & Precedents</h3>
                    <ul className="cases-list">
                      {researchResults.case_law.map((case_item, index) => (
                        <li key={index} dangerouslySetInnerHTML={{ __html: case_item }} />
                      ))}
                    </ul>
                  </div>
                )}

                {researchResults.recommendations && (
                  <div className="research-section">
                    <h3 className="section-title gold-text">Recommendations</h3>
                    <div dangerouslySetInnerHTML={{ __html: researchResults.recommendations }} />
                  </div>
                )}
        <ReportIssueBar toolName="Legal Research" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Account Settings Modal */}
      <AccountSettings
        open={accountSettingsOpen}
        onOpenChange={setAccountSettingsOpen}
        user={user}
        trialUser={trialUser}
        defaultTab={accountSettingsTab}
      />
    </div>
  );
};

export default LegalResearch;
